import React, { useState, useEffect, useCallback } from 'react';
import {
  FaSearch, FaDownload, FaUserGraduate, FaCheckCircle, FaClock,
  FaEye, FaTimes, FaEnvelope,
  FaGraduationCap, FaCreditCard, FaTrash,
  FaFilePdf, FaDesktop, FaHome, FaTimesCircle,
  FaChevronLeft, FaChevronRight, FaMoneyBillWave, FaEdit,
  FaStickyNote, FaBell, FaHistory, FaPaperPlane, FaCheckDouble,
  FaUser, FaCheck, FaExclamationTriangle, FaSpinner
} from 'react-icons/fa';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';

//  Config 
import { API_URL } from '../config/api';
const API = `${API_URL}/admin/registrations`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

const LOGO_SRC    = '../assets/images/Logo_CINEF.png';
const SB_OPEN     = 252;
const SB_CLOSED   =  74;
const SB_GAP      =  12;
const NB_HEIGHT   =  68;
const NB_TOP      =  12;
const PER_PAGE    =   8;

//  Constantes UI 
const C = { green:'#198754', orange:'#fd7e14', red:'#dc3545', teal:'#20c997', dark:'#1f2937' };

const STATUS_CFG = {
  confirmed: { label:'Confirmé',   color:C.green,  bg:'rgba(25,135,84,.08)',  border:'rgba(25,135,84,.2)'  },
  pending:   { label:'En attente', color:C.orange, bg:'rgba(253,126,20,.08)', border:'rgba(253,126,20,.2)' },
  cancelled: { label:'Annulé',     color:C.red,    bg:'rgba(220,53,69,.08)',  border:'rgba(220,53,69,.2)'  },
};
const PAY_CFG = {
  paid:    { label:'Payé',       color:C.green,  bg:'rgba(25,135,84,.08)'  },
  pending: { label:'En attente', color:C.orange, bg:'rgba(253,126,20,.08)' },
  failed:  { label:'Annulé',     color:C.red,    bg:'rgba(220,53,69,.08)'  },
};
const PAY_METHOD_LBL = { carte:'Carte bancaire', mobile:'Mobile Money', virement:'Virement bancaire', especes:'Espèces en caisse' };
const TYPE_LBL       = { presentiel:'Présentiel', elearning:'E-Learning', blended:'Blended Learning', ligne:'En ligne' };
const HISTO_CFG      = {
  recu:{ color:C.teal }, paid:{ color:C.green }, ok:{ color:C.green },
  wait:{ color:C.orange }, cancel:{ color:C.red }, note:{ color:'#6b7280' },
  conv:{ color:C.teal }, email:{ color:C.green },
};

const fmtDate  = d => d ? new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtPrice = p => new Intl.NumberFormat('fr-FR').format(p) + ' €';
const initials = i => `${(i.prenom||'?')[0]}${(i.nom||'?')[0]}`;

//  Générateur de facture (identique à l'original) 
const buildInvoiceHTML = (ins, lines) => {
  const logoTag = `<img src="${LOGO_SRC}" alt="CINEF" style="height:60px;object-fit:contain;display:block;margin-bottom:8px" onerror="this.style.display='none';document.getElementById('fb').style.display='block'"/>
    <div id="fb" style="display:none;font-family:Georgia,serif;font-size:30px;font-weight:900;color:#198754;letter-spacing:-1px;margin-bottom:8px">CINEF</div>`;
  const linesHTML = lines.map(l => `<tr><td style="padding:13px 14px;border-bottom:1px solid #f1f5f9;vertical-align:top"><strong style="display:block;color:#1f2937;font-size:14px;margin-bottom:3px">${l.desc}</strong><span style="font-size:12px;color:#9ca3af;white-space:pre-line">${l.sub}</span></td><td style="padding:13px 14px;border-bottom:1px solid #f1f5f9;text-align:center;font-weight:600">${l.qty}</td><td style="padding:13px 14px;border-bottom:1px solid #f1f5f9;text-align:right">${fmtPrice(l.pu)}</td><td style="padding:13px 14px;border-bottom:1px solid #f1f5f9;text-align:right;font-weight:800;color:#1f2937">${fmtPrice(l.qty*l.pu)}</td></tr>`).join('');
  const payColor = PAY_CFG[ins.paymentStatus]?.color||'#6b7280';
  const payLabel = PAY_CFG[ins.paymentStatus]?.label||ins.paymentStatus;
  
  // Calcul TVA 18%
  const totalHT = ins.totalPrice || 0;
  const tva = totalHT * 0.18;
  const totalTTC = totalHT + tva;
  
  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"/><title>Facture ${ins.id}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Helvetica Neue',Arial,sans-serif;color:#374151;background:#fff;font-size:13px;line-height:1.5}.page{max-width:820px;margin:0 auto;padding:40px}.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:36px}.inv-badge{display:inline-block;font-size:13px;font-weight:900;letter-spacing:3px;border:2px solid #1f2937;padding:7px 18px;border-radius:4px;margin-bottom:14px;color:#1f2937}.meta-tbl{border-collapse:collapse;font-size:12px;margin-left:auto}.meta-tbl td{padding:3px 10px}.meta-tbl .lbl{font-weight:700}.meta-tbl .val{color:#6b7280}hr{border:none;border-top:1px solid #e5e7eb;margin:28px 0}.parties{display:grid;grid-template-columns:1fr 1fr;gap:48px}.party-eye{font-size:10px;font-weight:800;color:#9ca3af;text-transform:uppercase;letter-spacing:1.2px;margin-bottom:8px}.party-name{font-weight:800;font-size:15px;color:#1f2937;margin-bottom:5px}.party p{font-size:12px;color:#6b7280;margin-bottom:3px}.lines-tbl{width:100%;border-collapse:collapse}.lines-tbl thead tr{background:#f9fafb}.lines-tbl th{padding:11px 14px;text-align:left;font-size:10px;font-weight:800;color:#9ca3af;text-transform:uppercase}.totals-wrap{width:300px;margin-left:auto;border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;margin-bottom:24px}.tot-row{display:flex;justify-content:space-between;padding:10px 16px;font-size:13px}.tot-grand{background:#198754;display:flex;justify-content:space-between;padding:14px 16px}.tot-grand .tgl{color:#fff;font-size:15px;font-weight:800}.tot-grand .vgl{color:#fff;font-size:17px;font-weight:900}.pay-note{font-size:12px;color:#4b5563;padding:14px 16px;background:#f0fdf4;border-left:3px solid #198754;border-radius:8px;margin-bottom:6px;line-height:1.8}.services-note{font-size:12px;color:#059669;padding:12px 16px;background:#f0fdf4;border-radius:8px;margin-bottom:16px;line-height:1.7}.footer-note{text-align:center;font-size:11px;color:#9ca3af;padding-top:8px}.dot{width:7px;height:7px;border-radius:50%;background:${payColor};display:inline-block}@media print{@page{margin:14mm}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body><div class="page"><div class="header"><div>${logoTag}<p style="font-size:12px;color:#6b7280;margin-bottom:2px">Centre International de Formation et d'Expertise</p><p style="font-size:11px;color:#9ca3af">info@cinef-bj.com · +229 63 18 60 60</p><p style="font-size:11px;color:#9ca3af">Cotonou, République du Bénin</p></div><div style="text-align:right"><div class="inv-badge">FACTURE</div><table class="meta-tbl"><tr><td class="lbl">Numéro</td><td class="val"><strong>${ins.id}</strong></td></tr><tr><td class="lbl">Date d'émission</td><td class="val">${fmtDate(ins.inscriptionDate)}</td></tr><tr><td class="lbl">Date formation</td><td class="val">${fmtDate(ins.startDate)}</td></tr><tr><td class="lbl">Mode règlement</td><td class="val">${PAY_METHOD_LBL[ins.modePaiement]||ins.modePaiement}</td></tr><tr><td class="lbl">Statut paiement</td><td class="val"><span style="display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:700"><span class="dot"></span>${payLabel}</span></td></tr></table></div></div><hr/><div class="parties"><div class="party"><p class="party-eye">Émetteur</p><p class="party-name">CINEF Bénin</p><p>Centre International de Formation et d'Expertise</p><p>Cotonou, Bénin | RCCM : BJ-COT-01-2020-B14</p><p>IFU : 3202214154682 · RC : RB/COT/21 B 2345</p><p>info@cinef-bj.com · +229 63 18 60 60</p></div><div class="party"><p class="party-eye">Destinataire</p><p class="party-name">${ins.civilite} ${ins.prenom} ${ins.nom}</p><p>${ins.poste} — ${ins.entreprise}</p><p>${ins.secteur}</p><p>${ins.ville}, ${ins.pays}</p><p>${ins.email} · ${ins.telephone}</p></div></div><hr/><table class="lines-tbl"><thead><tr><th style="width:45%">Description</th><th style="text-align:center">Qté</th><th style="text-align:right">Prix unitaire HT</th><th style="text-align:right">Montant HT</th></tr></thead><tbody>${linesHTML}</tbody></table><hr/><div class="totals-wrap"><div class="tot-row"><span style="color:#6b7280">Sous-total HT</span><span style="font-weight:700">${fmtPrice(totalHT)}</span></div><div class="tot-row"><span style="color:#6b7280">TVA (18 %)</span><span style="font-weight:700">${fmtPrice(tva)}</span></div><div class="tot-grand"><span class="tgl">TOTAL TTC</span><span class="vgl">${fmtPrice(totalTTC)}</span></div></div><div class="services-note"><strong>✓ Services inclus dans votre formation :</strong><br/>• Pauses-café quotidiennes (matin et après-midi)<br/>• Déjeuner buffet tous les jours<br/>• Support de cours complet (version papier + numérique)<br/>• Accès plateforme e-learning CINEF pendant 6 mois<br/>• Attestation de participation</div><div class="pay-note"><strong>Mode de règlement :</strong> ${PAY_METHOD_LBL[ins.modePaiement]||ins.modePaiement}<br/><strong>Conditions de paiement :</strong> Paiement intégral exigible avant le démarrage de la formation (${fmtDate(ins.startDate)}).<br/><strong>Certificat de formation :</strong> ${ins.certificat?"Inclus — remis à l'issue de la formation (sous réserve d'assiduité à 80% minimum).":'Disponible sur demande (frais supplémentaires applicables).'}</div><hr/><p class="footer-note">Merci pour votre confiance. CINEF — Centre International de Formation et d'Expertise<br/>Document généré le ${new Date().toLocaleDateString('fr-FR')} · TVA 18% conformément à la réglementation en vigueur</p></div><script>window.onload=()=>{window.focus();window.print()}<\/script></body></html>`;
};

// ─

const Inscriptions = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  //  Données 
  const [list,        setList]        = useState([]);
  const [stats,       setStats]       = useState({ total:0, confirmed:0, pending:0, cancelled:0, revenue:0 });
  const [meta,        setMeta]        = useState({ current_page:1, last_page:1, total:0 });
  const [loading,     setLoading]     = useState(true);
  const [modalLoading,setModalLoading]= useState(false);
  const [actionLoad,  setActionLoad]  = useState(''); // ex: "123_status"

  //  Filtres 
  const [searchQuery,  setSearchQuery]  = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment,setFilterPayment]= useState('all');
  const [filterType,   setFilterType]   = useState('all');
  const [currentPage,  setCurrentPage]  = useState(1);

  //  Modal 
  const [selectedIns, setSelectedIns] = useState(null);
  const [modalTab,    setModalTab]    = useState('details');
  const [showModal,   setShowModal]   = useState(false);
  const [noteText,    setNoteText]    = useState('');

  //  Toast 
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  //  Thème 
  const c = isDark ? {
    bg:'#090c16', card:'rgba(255,255,255,0.04)', cardB:'rgba(255,255,255,0.08)',
    text:'#c8d4e0', textDim:'#5a6b82', title:'#eef2f7', surface:'rgba(255,255,255,0.04)',
    shadow:'0 4px 24px rgba(0,0,0,0.4)', input:'rgba(255,255,255,0.06)', inputB:'rgba(255,255,255,0.12)',
    thead:'rgba(255,255,255,0.07)', theadText:'rgba(255,255,255,0.45)', rowHover:'rgba(255,255,255,0.03)',
    rowBorder:'rgba(255,255,255,0.06)', modalBg:'#0f172a', modalHead:'rgba(255,255,255,0.03)',
    tabBg:'rgba(255,255,255,0.06)', tabOn:'rgba(255,255,255,0.14)', noteArea:'rgba(255,255,255,0.05)',
    blockBg:'rgba(255,255,255,0.04)', blockHd:'rgba(255,255,255,0.06)',
    pagerBg:'rgba(255,255,255,0.05)', pagerB:'rgba(255,255,255,0.1)',
  } : {
    bg:'#f0f3f8', card:'#ffffff', cardB:'rgba(0,0,0,0.07)', text:'#4d6070',
    textDim:'#8fa3b5', title:'#1a2535', surface:'rgba(0,0,0,0.04)',
    shadow:'0 2px 12px rgba(0,0,0,0.05)', input:'#f7f9fb', inputB:'rgba(0,0,0,0.08)',
    thead:'#1a2535', theadText:'rgba(255,255,255,0.5)', rowHover:'#fafbfc',
    rowBorder:'rgba(0,0,0,0.055)', modalBg:'#ffffff', modalHead:'#fafbfc',
    tabBg:'#ffffff', tabOn:'#1a2535', noteArea:'#ffffff', blockBg:'#f7f9fb',
    blockHd:'#ffffff', pagerBg:'#ffffff', pagerB:'rgba(0,0,0,0.09)',
  };

  //  Chargement de la liste ─
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page:           currentPage,
        per_page:       PER_PAGE,
        search:         searchQuery || undefined,
        status:         filterStatus  !== 'all' ? filterStatus  : undefined,
        payment_status: filterPayment !== 'all' ? filterPayment : undefined,
        type:           filterType    !== 'all' ? filterType    : undefined,
      };
      const res = await axios.get(API, { ...getAuth(), params });
      setList(res.data.data);
      setStats(res.data.stats);
      setMeta(res.data.meta);
    } catch (e) {
      showToast('Erreur lors du chargement des inscriptions.', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, filterStatus, filterPayment, filterType]);

  useEffect(() => { loadList(); }, [loadList]);

  // Debounce recherche
  useEffect(() => {
    const t = setTimeout(() => { setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  //  Chargement modal ─
  const openModal = async (ins, tab = 'details') => {
    setModalTab(tab);
    setShowModal(true);
    setNoteText('');
    setModalLoading(true);
    try {
      const res = await axios.get(`${API}/${ins.db_id}`, getAuth());
      setSelectedIns(res.data);
    } catch {
      showToast('Erreur lors du chargement des détails.', 'error');
      setShowModal(false);
    } finally {
      setModalLoading(false);
    }
  };
  const closeModal = () => { setShowModal(false); setSelectedIns(null); };

  //  Changer statut inscription ─
  const changeStatus = async (dbId, status) => {
    setActionLoad(`${dbId}_status`);
    try {
      const res = await axios.patch(`${API}/${dbId}/status`, { status }, getAuth());
      const updated = res.data.data;
      setSelectedIns(updated);
      setList(p => p.map(i => i.db_id === dbId ? { ...i, status: updated.status, historique: updated.historique } : i));
      showToast('Statut mis à jour.');
    } catch (e) {
      showToast(e.response?.data?.message || 'Erreur.', 'error');
    } finally {
      setActionLoad('');
    }
  };

  //  Changer statut paiement 
  const changePayment = async (dbId, status) => {
    setActionLoad(`${dbId}_payment`);
    try {
      const res = await axios.patch(`${API}/${dbId}/payment`, { status }, getAuth());
      setSelectedIns(p => p ? { ...p, paymentStatus: res.data.payment_status } : p);
      setList(prev => prev.map(i => i.db_id === dbId ? { ...i, paymentStatus: res.data.payment_status } : i));
      showToast('Statut paiement mis à jour.');
    } catch (e) {
      showToast(e.response?.data?.message || 'Erreur.', 'error');
    } finally {
      setActionLoad('');
    }
  };

  //  Ajouter une note ─
  const addNote = async (dbId) => {
    if (!noteText.trim()) return;
    setActionLoad(`${dbId}_note`);
    try {
      const res = await axios.post(`${API}/${dbId}/notes`, { content: noteText.trim() }, getAuth());
      setSelectedIns(p => p ? { ...p, notes: [res.data.note, ...(p.notes||[])] } : p);
      setNoteText('');
      showToast('Note enregistrée.');
    } catch (e) {
      showToast(e.response?.data?.message || 'Erreur.', 'error');
    } finally {
      setActionLoad('');
    }
  };

  //  Envoyer convocation 
  const sendConvocation = async (dbId) => {
    setActionLoad(`${dbId}_conv`);
    try {
      const res = await axios.post(`${API}/${dbId}/convocation`, {}, getAuth());
      setSelectedIns(p => p ? { ...p, convocationSent: true } : p);
      showToast(res.data.message);
    } catch (e) {
      showToast(e.response?.data?.message || 'Erreur lors de l\'envoi.', 'error');
    } finally {
      setActionLoad('');
    }
  };

  //  Supprimer 
  const deleteIns = async (dbId) => {
    if (!window.confirm('Supprimer définitivement cette inscription ?')) return;
    try {
      await axios.delete(`${API}/${dbId}`, getAuth());
      setList(p => p.filter(i => i.db_id !== dbId));
      setStats(p => ({ ...p, total: p.total - 1 }));
      closeModal();
      showToast('Inscription supprimée.');
    } catch (e) {
      showToast(e.response?.data?.message || 'Erreur.', 'error');
    }
  };

  //  Export CSV ─
  const exportCSV = async () => {
    try {
      const params = {
        status: filterStatus  !== 'all' ? filterStatus  : undefined,
        type:   filterType    !== 'all' ? filterType    : undefined,
      };
      const res = await axios.get(`${API}/export`, {
        ...getAuth(), params, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `inscriptions_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Erreur lors de l\'export CSV.', 'error');
    }
  };

  //  Facture 
  const getInvoiceLines = ins => {
    // Calculer le prix unitaire HT de la formation (en enlevant les suppléments)
    let totalSupplements = 0;
    if (ins.hebergement) totalSupplements += 75 * ins.nombrePersonnes;
    if (ins.transport) totalSupplements += 40 * ins.nombrePersonnes;
    
    const totalHT = ins.totalPrice || 0;
    const formationHT = totalHT - totalSupplements;
    const puFormation = ins.nombrePersonnes > 0 ? formationHT / ins.nombrePersonnes : 0;
    
    // Ligne formation avec indication des services inclus
    const lines = [{ 
      desc: `${ins.formation} — ${ins.themeFormation}`, 
      sub: `${TYPE_LBL[ins.typeFormation]||ins.typeFormation} · ${ins.duration||''} · à ${ins.ville}\n✓ Pauses-café et Déjeuner offerts · ✓ Support de cours`, 
      qty: ins.nombrePersonnes, 
      pu: puFormation 
    }];
    
    // Suppléments payants uniquement
    if (ins.hebergement) lines.push({ desc:'Hébergement', sub:'Nuitées par participant', qty:ins.nombrePersonnes, pu:75 });
    if (ins.transport) lines.push({ desc:'Transport aller-retour', sub:'Navette par participant', qty:ins.nombrePersonnes, pu:40 });
    
    return lines;
  };
  const openInvoice = ins => {
    const w = window.open('', '_blank', 'width=860,height=700,scrollbars=yes');
    if (!w) { alert('Autorisez les popups.'); return; }
    w.document.write(buildInvoiceHTML(ins, getInvoiceLines(ins)));
    w.document.close();
  };

  //  Helpers UI ─
  const avColor   = s => s === 'confirmed' ? C.green : s === 'pending' ? C.orange : C.red;
  const isLoading = key => actionLoad === key;
  const mainML    = `${SB_GAP + (sidebarOpen ? SB_OPEN : SB_CLOSED) + 12}px`;
  const mainPT    = `${NB_TOP + NB_HEIGHT + 12}px`;
  const totalPages = meta.last_page;
  const goPage     = p => setCurrentPage(Math.max(1, Math.min(p, totalPages)));

  //  Rendu 
  return (
    <div style={{ background:c.bg, minHeight:'100vh', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
        *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;font-family:'Plus Jakarta Sans',sans-serif;}
        .ins-main{transition:margin-left .28s cubic-bezier(.4,0,.2,1);min-height:100vh;}
        .ins-body{padding:22px 24px 40px;}
        .ins-pg-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:22px;gap:16px;flex-wrap:wrap;}
        .ins-pg-left{display:flex;align-items:center;gap:14px;}
        .ins-pg-icon{width:48px;height:48px;border-radius:13px;background:#198754;color:#fff;display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0;}
        .ins-export-btn{display:flex;align-items:center;gap:8px;padding:10px 20px;background:transparent;color:#198754;border:1.5px solid rgba(25,135,84,.35);border-radius:11px;font-size:14px;font-weight:600;cursor:pointer;transition:all .2s;white-space:nowrap;}
        .ins-export-btn:hover{background:rgba(25,135,84,.07);border-color:#198754;}
        .ins-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:20px;}
        .ins-kpi{border-radius:14px;padding:20px 18px;border:1px solid;position:relative;overflow:hidden;transition:transform .22s,box-shadow .22s;cursor:default;}
        .ins-kpi:hover{transform:translateY(-3px);}
        .ins-kpi-deco{position:absolute;right:-16px;top:-16px;width:72px;height:72px;border-radius:50%;opacity:.07;}
        .ins-kpi-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;}
        .ins-kpi-icon{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:18px;border:1px solid;}
        .ins-fbar{border-radius:13px;border:1px solid;padding:14px 18px;margin-bottom:18px;display:flex;gap:12px;align-items:center;flex-wrap:wrap;}
        .ins-fsearch{flex:1;min-width:260px;display:flex;align-items:center;gap:10px;border:1.5px solid;border-radius:10px;padding:0 16px;height:42px;transition:border-color .2s;}
        .ins-fsearch:focus-within{border-color:#198754!important;}
        .ins-fsearch input{flex:1;border:none;outline:none;font-size:14px;background:transparent;}
        .ins-selects{display:flex;gap:8px;}
        .ins-selects select{height:42px;padding:0 12px;border:1.5px solid;border-radius:10px;font-size:13.5px;font-weight:500;cursor:pointer;outline:none;transition:border-color .2s;}
        .ins-selects select:focus{border-color:#198754!important;}
        .ins-tcard{border-radius:14px;border:1px solid;overflow:hidden;}
        .ins-tscroll{overflow-x:auto;}
        .ins-tbl{width:100%;border-collapse:collapse;min-width:1050px;}
        .ins-tbl th{padding:13px 18px;text-align:left;font-size:10.5px;font-weight:600;text-transform:uppercase;letter-spacing:.7px;white-space:nowrap;}
        .ins-trow{border-bottom:1px solid;transition:background .14s;}
        .ins-trow:last-child{border-bottom:none;}
        .ins-tbl td{padding:13px 18px;vertical-align:middle;}
        .ins-pcell{display:flex;align-items:center;gap:11px;}
        .ins-pav{width:36px;height:36px;border-radius:10px;flex-shrink:0;color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;}
        .ins-tchip{display:inline-flex;align-items:center;gap:5px;font-size:11.5px;font-weight:600;padding:4px 10px;border-radius:7px;}
        .ins-tc-presentiel{background:rgba(25,135,84,.08);color:#198754;}
        .ins-tc-elearning{background:rgba(253,126,20,.08);color:#fd7e14;}
        .ins-tc-blended{background:rgba(32,201,151,.08);color:#20c997;}
        .ins-tc-ligne{background:rgba(253,126,20,.08);color:#fd7e14;}
        .ins-nball{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:50%;font-weight:700;font-size:13px;}
        .ins-bdot{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;padding:4px 10px;border-radius:20px;}
        .ins-bdot i{width:6px;height:6px;border-radius:50%;display:inline-block;flex-shrink:0;}
        .ins-bpill{display:inline-block;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;border:1px solid;}
        .ins-ract{display:flex;gap:5px;}
        .ins-rb{width:32px;height:32px;border-radius:8px;border:1.5px solid;display:flex;align-items:center;justify-content:center;font-size:12px;cursor:pointer;transition:all .18s;}
        .ins-rb-green:hover{background:rgba(25,135,84,.08)!important;border-color:#198754!important;color:#198754!important;}
        .ins-rb-orange:hover{background:rgba(253,126,20,.08)!important;border-color:#fd7e14!important;color:#fd7e14!important;}
        .ins-rb-blue:hover{background:rgba(13,110,253,.08)!important;border-color:#0d6efd!important;color:#0d6efd!important;}
        .ins-rb-red:hover{background:rgba(220,53,69,.08)!important;border-color:#dc3545!important;color:#dc3545!important;}
        .ins-pager{display:flex;align-items:center;gap:6px;padding:16px 18px;border-top:1px solid;flex-wrap:wrap;}
        .ins-pbt{min-width:36px;height:36px;border-radius:9px;border:1.5px solid;font-size:13.5px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:4px;transition:all .18s;padding:0 8px;}
        .ins-pbt:hover:not(:disabled){border-color:#198754!important;color:#198754!important;}
        .ins-pbt-active{background:#198754!important;color:#fff!important;border-color:#198754!important;}
        .ins-pbt:disabled{opacity:.3;cursor:default;}
        .ins-mbg{position:fixed;inset:0;background:rgba(15,23,42,.6);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:2000;padding:20px;animation:ins-mf .22s ease;}
        @keyframes ins-mf{from{opacity:0}to{opacity:1}}
        .ins-mbox{border-radius:20px;width:100%;max-width:920px;max-height:92vh;display:flex;flex-direction:column;box-shadow:0 24px 64px rgba(0,0,0,.18);overflow:hidden;animation:ins-ms .28s cubic-bezier(.34,1.56,.64,1);}
        @keyframes ins-ms{from{opacity:0;transform:translateY(32px)}to{opacity:1;transform:none}}
        .ins-mhead{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-bottom:1px solid;flex-shrink:0;}
        .ins-mtabs{display:flex;gap:6px;}
        .ins-mtab{display:flex;align-items:center;gap:7px;padding:8px 16px;border-radius:9px;border:1.5px solid;font-size:13px;font-weight:600;cursor:pointer;transition:all .18s;}
        .ins-mcx{width:36px;height:36px;border-radius:9px;border:1.5px solid;display:flex;align-items:center;justify-content:center;font-size:14px;cursor:pointer;transition:all .18s;}
        .ins-mcx:hover{background:#dc3545!important;color:#fff!important;border-color:#dc3545!important;}
        .ins-mscroll{overflow-y:auto;flex:1;}
        .ins-mpad{padding:24px 28px;}
        .ins-dhero{display:flex;align-items:center;gap:18px;border:1px solid;border-radius:14px;padding:20px;margin-bottom:20px;}
        .ins-dhav{width:56px;height:56px;border-radius:13px;flex-shrink:0;color:#fff;font-size:18px;font-weight:700;display:flex;align-items:center;justify-content:center;}
        .ins-dhamount{font-size:24px;font-weight:700;color:#198754;flex-shrink:0;margin-left:auto;}
        .ins-dg2{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px;}
        .ins-dbl{border:1px solid;border-radius:13px;overflow:hidden;margin-bottom:14px;}
        .ins-dbl-t{display:flex;align-items:center;gap:9px;padding:11px 15px;border-bottom:1px solid;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.7px;border-left:3px solid;}
        .ins-drows{display:grid;grid-template-columns:1fr 1fr;gap:0;padding:12px 14px 6px;}
        .ins-drow{padding:6px;display:flex;flex-direction:column;gap:3px;}
        .ins-drow.dfull{grid-column:1/-1;}
        .ins-drow label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;}
        .ins-drow span{font-size:13.5px;font-weight:600;display:flex;align-items:center;gap:6px;}
        .ins-svcrow{display:flex;gap:10px;padding:12px 14px;flex-wrap:wrap;}
        .ins-svc{display:inline-flex;align-items:center;gap:6px;padding:7px 12px;border-radius:8px;font-size:12.5px;font-weight:600;}
        .ins-svc-y{background:rgba(25,135,84,.08);color:#198754;}
        .ins-motiv{padding:13px 16px;font-size:13.5px;line-height:1.7;font-style:italic;border-left:3px solid #fd7e14;margin:12px 14px;background:rgba(253,126,20,.04);border-radius:0 8px 8px 0;}
        .ins-wf-recap{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:16px 18px;background:rgba(25,135,84,.05);border:1px solid rgba(25,135,84,.15);border-radius:13px;margin-bottom:20px;flex-wrap:wrap;}
        .ins-wf-recap-left{display:flex;align-items:center;gap:12px;}
        .ins-wf-bell{font-size:26px;color:#198754;flex-shrink:0;}
        .ins-wf-ref{font-size:12px;font-weight:700;color:#198754;margin-left:5px;}
        .ins-wf-sec{border:1px solid;border-radius:13px;padding:18px;margin-bottom:16px;}
        .ins-wf-sec-t{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.7px;margin-bottom:14px;display:flex;align-items:center;gap:8px;}
        .ins-wf-sec-t svg{color:#198754;}
        .ins-wf-checklist{display:flex;flex-direction:column;gap:8px;}
        .ins-wfcli{display:flex;align-items:center;gap:11px;padding:11px 14px;border-radius:10px;border:1px solid;font-size:13.5px;font-weight:500;}
        .ins-wfcli-done{background:rgba(25,135,84,.06);border-color:rgba(25,135,84,.18);color:#198754;}
        .ins-wfcli-pending{background:rgba(253,126,20,.05);border-color:rgba(253,126,20,.18);color:#fd7e14;}
        .ins-wfcli-dot{width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;flex-shrink:0;}
        .ins-wfcli-done .ins-wfcli-dot{background:rgba(25,135,84,.12);}
        .ins-wfcli-pending .ins-wfcli-dot{background:rgba(253,126,20,.10);}
        .ins-wf-brow{display:flex;gap:10px;flex-wrap:wrap;}
        .ins-wfb{display:inline-flex;align-items:center;gap:7px;padding:10px 16px;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
        .ins-wfb:disabled{opacity:.5;cursor:not-allowed;}
        .ins-wfb-green{background:rgba(25,135,84,.08);color:#198754;border:1.5px solid rgba(25,135,84,.2);}
        .ins-wfb-green:hover:not(:disabled),.ins-wfb-green.wfb-on{background:#198754;color:#fff;border-color:#198754;}
        .ins-wfb-orange{background:rgba(253,126,20,.08);color:#fd7e14;border:1.5px solid rgba(253,126,20,.2);}
        .ins-wfb-orange:hover:not(:disabled),.ins-wfb-orange.wfb-on{background:#fd7e14;color:#fff;border-color:#fd7e14;}
        .ins-wfb-red{background:rgba(220,53,69,.08);color:#dc3545;border:1.5px solid rgba(220,53,69,.2);}
        .ins-wfb-red:hover:not(:disabled),.ins-wfb-red.wfb-on{background:#dc3545;color:#fff;border-color:#dc3545;}
        .ins-wfb-teal{background:rgba(32,201,151,.08);color:#20c997;border:1.5px solid rgba(32,201,151,.2);}
        .ins-wfb-teal:hover:not(:disabled),.ins-wfb-teal.wfb-on{background:#20c997;color:#fff;border-color:#20c997;}
        .ins-wfb-dark{background:#1a2535;color:#fff;border:1.5px solid #1a2535;}
        .ins-wfb-dark:hover:not(:disabled){background:#2d3748;border-color:#2d3748;}
        .ins-wfb-out-red{background:transparent;color:#dc3545;border:1.5px solid rgba(220,53,69,.3);}
        .ins-wfb-out-red:hover:not(:disabled){background:#dc3545;color:#fff;border-color:#dc3545;}
        .ins-wfb-cur{background:rgba(255,255,255,.22);border-radius:4px;padding:1px 5px;font-size:10px;margin-left:3px;}
        .ins-note{border:1px solid;border-radius:10px;padding:13px;margin-bottom:9px;}
        .ins-note-h{display:flex;justify-content:space-between;margin-bottom:5px;}
        .ins-note-auth{font-size:12px;font-weight:700;color:#198754;}
        .ins-timeline{display:flex;flex-direction:column;}
        .ins-tli{display:flex;gap:13px;position:relative;padding-bottom:16px;}
        .ins-tli:last-child{padding-bottom:0;}
        .ins-tlidot{width:30px;height:30px;border-radius:50%;border:2px solid;display:flex;align-items:center;justify-content:center;font-size:9px;flex-shrink:0;}
        .ins-tliline{position:absolute;left:14px;top:32px;bottom:0;width:2px;}
        .ins-tlic{display:flex;flex-direction:column;gap:2px;padding-top:4px;}
        .ins-wf-danger{border-color:rgba(220,53,69,.15)!important;}
        .ins-inv-actbar{display:flex;gap:10px;margin-bottom:12px;flex-wrap:wrap;}
        .ins-inv-hint{display:flex;align-items:center;gap:10px;padding:11px 14px;background:rgba(253,126,20,.06);border:1px solid rgba(253,126,20,.18);border-radius:10px;font-size:13px;margin-bottom:18px;}
        .ins-inv-preview{border:1px solid;border-radius:14px;padding:32px;}
        .ins-inv-head{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;}
        .ins-inv-logo{height:48px;object-fit:contain;display:block;margin-bottom:7px;}
        .ins-inv-logo-fb{display:none;font-size:26px;font-weight:900;color:#198754;margin-bottom:7px;}
        .ins-inv-badge{display:inline-block;font-size:12px;font-weight:900;letter-spacing:3px;border:2px solid;padding:6px 14px;border-radius:4px;margin-bottom:11px;}
        .ins-inv-meta-t{border-collapse:collapse;font-size:12px;margin-left:auto;}
        .ins-inv-meta-t td{padding:2px 8px;}
        .ins-inv-rule{border:none;border-top:1px solid;margin:18px 0;}
        .ins-inv-parties{display:grid;grid-template-columns:1fr 1fr;gap:36px;}
        .ins-inv-pe{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;margin-bottom:6px;}
        .ins-inv-pn{font-weight:700;font-size:14px;margin-bottom:4px;}
        .ins-inv-parties p{font-size:12px;margin-bottom:2px;}
        .ins-inv-ltbl{width:100%;border-collapse:collapse;}
        .ins-inv-ltbl th{padding:10px 12px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;}
        .ins-inv-ltbl td{padding:11px 12px;border-bottom:1px solid;font-size:13px;vertical-align:top;}
        .ins-inv-ln{display:block;font-size:13px;font-weight:700;margin-bottom:2px;}
        .ins-inv-ls{display:block;font-size:11px;}
        .ins-inv-tots{width:260px;margin-left:auto;border:1px solid;border-radius:10px;overflow:hidden;margin-bottom:18px;}
        .ins-inv-tr{display:flex;justify-content:space-between;padding:9px 14px;font-size:13px;}
        .ins-inv-tr span:last-child{font-weight:700;}
        .ins-inv-tgrand{display:flex;justify-content:space-between;padding:13px 14px;background:#1a2535;}
        .ins-inv-tgrand span{color:#fff;font-size:14px;font-weight:700;}
        .ins-inv-tgrand span:last-child{color:#20c997;}
        .ins-inv-foot{font-size:11px;text-align:center;padding-top:4px;}
        @keyframes spin{to{transform:rotate(360deg)}}
        @media(max-width:1200px){.ins-kpis{grid-template-columns:repeat(2,1fr);}}
        @media(max-width:768px){.ins-body{padding:14px 14px 32px;}.ins-kpis{grid-template-columns:1fr 1fr;}.ins-fbar{flex-direction:column;align-items:stretch;}.ins-fsearch{min-width:100%;}.ins-selects{flex-wrap:wrap;}.ins-selects select{flex:1;min-width:110px;}.ins-mbox{max-width:100%;border-radius:20px 20px 0 0;align-self:flex-end;max-height:95vh;}.ins-dg2,.ins-inv-parties{grid-template-columns:1fr;}.ins-dhero{flex-direction:column;text-align:center;}.ins-dhamount{margin-left:0;}.ins-drows{grid-template-columns:1fr;}.ins-inv-tots{width:100%;}.ins-wf-brow,.ins-inv-actbar{flex-direction:column;}}
        @media(max-width:480px){.ins-kpis{grid-template-columns:1fr;}}
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', bottom:24, right:24, zIndex:9999, display:'flex', alignItems:'center', gap:12,
          padding:'14px 20px', borderRadius:12, fontWeight:600, fontSize:14,
          background: toast.type === 'success' ? '#198754' : '#dc3545', color:'#fff',
          boxShadow:'0 8px 24px rgba(0,0,0,.2)', animation:'ins-mf .3s ease' }}>
          {toast.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
          {toast.msg}
          <button onClick={() => setToast(null)} style={{ background:'none', border:'none', color:'#fff', cursor:'pointer', marginLeft:4 }}>
            <FaTimes />
          </button>
        </div>
      )}

      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Navbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} pageTitle="Inscriptions" adminName="Jean Dupont" />

      <main className="ins-main" style={{ marginLeft:mainML, paddingTop:mainPT }}>
        <div className="ins-body">

          {/* Page top */}
          <div className="ins-pg-top">
            <div className="ins-pg-left">
              <div className="ins-pg-icon"><FaUserGraduate /></div>
              <div>
                <h1 style={{ fontSize:20, fontWeight:700, color:c.title, marginBottom:2 }}>Gestion des Inscriptions</h1>
                <p  style={{ fontSize:13, color:c.textDim }}>Validation, suivi et facturation des inscriptions aux formations</p>
              </div>
            </div>
            <button className="ins-export-btn" onClick={exportCSV}><FaDownload /> Exporter CSV</button>
          </div>

          {/* KPIs */}
          <div className="ins-kpis">
            {[
              { label:'Total inscriptions', val:stats.total,             color:'#1a2535', iconBg:'rgba(31,41,55,.1)',    icon:<FaUserGraduate /> },
              { label:'Confirmées',          val:stats.confirmed,         color:C.green,  iconBg:'rgba(25,135,84,.1)',   icon:<FaCheckCircle /> },
              { label:'En attente',          val:stats.pending,           color:C.orange, iconBg:'rgba(253,126,20,.1)', icon:<FaClock /> },
              { label:'Revenus encaissés',  val:fmtPrice(stats.revenue), color:C.teal,   iconBg:'rgba(32,201,151,.1)', icon:<FaMoneyBillWave /> },
            ].map((k,i) => (
              <div key={i} className="ins-kpi" style={{ background:c.card, borderColor:c.cardB, boxShadow:c.shadow }}>
                <div className="ins-kpi-deco" style={{ background:k.color }} />
                <div className="ins-kpi-top">
                  <div className="ins-kpi-icon" style={{ background:k.iconBg, color:k.color, borderColor:`${k.color}22` }}>{k.icon}</div>
                </div>
                <div style={{ fontSize:13, fontWeight:500, color:c.textDim, marginBottom:5 }}>{k.label}</div>
                <div style={{ fontSize:i===3?'20px':'30px', fontWeight:700, color:k.color, letterSpacing:-0.5, lineHeight:1 }}>{k.val}</div>
              </div>
            ))}
          </div>

          {/* Filtres */}
          <div className="ins-fbar" style={{ background:c.card, borderColor:c.cardB, boxShadow:c.shadow }}>
            <div className="ins-fsearch" style={{ background:c.input, borderColor:c.inputB }}>
              <FaSearch style={{ color:c.textDim, fontSize:13, flexShrink:0 }} />
              <input placeholder="Rechercher par nom, email, formation, réf…"
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                style={{ color:c.title }} />
            </div>
            <div className="ins-selects">
              {[
                { val:filterStatus,  set:v=>{setFilterStatus(v);setCurrentPage(1);},
                  opts:[['all','Tous statuts'],['confirmed','Confirmé'],['pending','En attente'],['cancelled','Annulé']] },
                { val:filterPayment, set:v=>{setFilterPayment(v);setCurrentPage(1);},
                  opts:[['all','Tous paiements'],['paid','Payé'],['pending','En attente'],['failed','Annulé']] },
                { val:filterType,    set:v=>{setFilterType(v);setCurrentPage(1);},
                  opts:[['all','Tous les types'],['presentiel','Présentiel'],['ligne','En ligne'],['elearning','E-Learning'],['blended','Blended']] },
              ].map((s,i) => (
                <select key={i} value={s.val} onChange={e=>s.set(e.target.value)}
                  style={{ background:c.input, borderColor:c.inputB, color:c.text }}>
                  {s.opts.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ))}
            </div>
            <span style={{ fontSize:13, fontWeight:600, color:c.textDim, whiteSpace:'nowrap', marginLeft:'auto' }}>
              {meta.total} résultat{meta.total>1?'s':''}
            </span>
          </div>

          {/* Tableau */}
          <div className="ins-tcard" style={{ background:c.card, borderColor:c.cardB, boxShadow:c.shadow }}>
            <div className="ins-tscroll">
              <table className="ins-tbl">
                <thead>
                  <tr style={{ background:c.thead }}>
                    {['Participant','Formation','Type','Pers.','Date début','Montant','Paiement','Statut','Actions'].map((h,i)=>(
                      <th key={i} style={{ color:c.theadText, textAlign:i===3||i===8?'center':'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="9" style={{ textAlign:'center', padding:'60px 20px', color:c.textDim }}>
                      <FaSpinner style={{ fontSize:32, display:'block', margin:'0 auto 12px', animation:'spin 1s linear infinite' }} />
                      <p style={{ fontSize:14 }}>Chargement…</p>
                    </td></tr>
                  ) : list.length ? list.map(ins => {
                    const st  = STATUS_CFG[ins.status];
                    const pay = PAY_CFG[ins.paymentStatus];
                    return (
                      <tr key={ins.db_id} className="ins-trow"
                        style={{ borderBottomColor:c.rowBorder }}
                        onMouseEnter={e=>e.currentTarget.style.background=c.rowHover}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td>
                          <div className="ins-pcell">
                            <div className="ins-pav" style={{ background:avColor(ins.status) }}>{initials(ins)}</div>
                            <div>
                              <div style={{ fontSize:13.5, fontWeight:600, color:c.title }}>{ins.civilite} {ins.prenom} {ins.nom}</div>
                              <div style={{ fontSize:11.5, color:c.textDim, marginTop:1 }}>{ins.entreprise}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize:13.5, fontWeight:600, color:c.title }}>{ins.formation}</div>
                          <div style={{ fontSize:11.5, color:c.textDim, marginTop:1 }}>{ins.themeFormation}</div>
                        </td>
                        <td>
                          <span className={`ins-tchip ins-tc-${ins.typeFormation}`}>
                            {ins.typeFormation==='presentiel'?<FaHome />:<FaDesktop />}
                            {TYPE_LBL[ins.typeFormation]||ins.typeFormation}
                          </span>
                        </td>
                        <td style={{ textAlign:'center' }}>
                          <span className="ins-nball" style={{ background:c.surface, color:c.title }}>{ins.nombrePersonnes}</span>
                        </td>
                        <td>
                          <div style={{ fontSize:13.5, fontWeight:600, color:c.title }}>{fmtDate(ins.startDate)}</div>
                          <div style={{ fontSize:11.5, color:c.textDim, marginTop:1 }}>{ins.ville}, {ins.pays}</div>
                        </td>
                        <td><span style={{ fontSize:13.5, fontWeight:700, color:c.title }}>{fmtPrice(ins.totalPrice)}</span></td>
                        <td>
                          <span className="ins-bdot" style={{ color:pay?.color, background:pay?.bg }}>
                            <i style={{ background:pay?.color }}/>{pay?.label}
                          </span>
                        </td>
                        <td>
                          <span className="ins-bpill" style={{ color:st?.color, background:st?.bg, borderColor:st?.border }}>{st?.label}</span>
                        </td>
                        <td>
                          <div className="ins-ract" style={{ justifyContent:'center' }}>
                            <button className="ins-rb ins-rb-green"  style={{ background:c.card, borderColor:c.cardB }} title="Voir détails"  onClick={()=>openModal(ins,'details')}><FaEye /></button>
                            <button className="ins-rb ins-rb-orange" style={{ background:c.card, borderColor:c.cardB }} title="Traiter"       onClick={()=>openModal(ins,'workflow')}><FaEdit /></button>
                            <button className="ins-rb ins-rb-blue"   style={{ background:c.card, borderColor:c.cardB }} title="Facture"       onClick={()=>openModal(ins,'facture')}><FaFilePdf /></button>
                            <button className="ins-rb ins-rb-red"    style={{ background:c.card, borderColor:c.cardB }} title="Supprimer"    onClick={()=>deleteIns(ins.db_id)}><FaTrash /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="9" style={{ textAlign:'center', padding:'60px 20px', color:c.textDim }}>
                      <FaUserGraduate style={{ fontSize:44, display:'block', margin:'0 auto 12px', opacity:.3 }} />
                      <p style={{ fontSize:15, fontWeight:600 }}>Aucune inscription ne correspond aux filtres</p>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="ins-pager" style={{ borderTopColor:c.rowBorder }}>
                <button className="ins-pbt" style={{ background:c.pagerBg, borderColor:c.pagerB, color:c.text }} onClick={()=>goPage(1)} disabled={currentPage===1}>«</button>
                <button className="ins-pbt" style={{ background:c.pagerBg, borderColor:c.pagerB, color:c.text }} onClick={()=>goPage(currentPage-1)} disabled={currentPage===1}><FaChevronLeft /></button>
                {Array.from({length:totalPages},(_,i)=>i+1)
                  .filter(p=>p===1||p===totalPages||Math.abs(p-currentPage)<=1)
                  .reduce((acc,p,idx,arr)=>{ if(idx>0&&arr[idx-1]!==p-1) acc.push('…'); acc.push(p); return acc; },[])
                  .map((p,idx) => p==='…'
                    ? <span key={`e${idx}`} style={{ padding:'0 4px', color:c.textDim, fontWeight:600 }}>…</span>
                    : <button key={p} className={`ins-pbt ${currentPage===p?'ins-pbt-active':''}`}
                        style={currentPage===p?{}:{ background:c.pagerBg, borderColor:c.pagerB, color:c.text }}
                        onClick={()=>goPage(p)}>{p}</button>
                  )}
                <button className="ins-pbt" style={{ background:c.pagerBg, borderColor:c.pagerB, color:c.text }} onClick={()=>goPage(currentPage+1)} disabled={currentPage===totalPages}><FaChevronRight /></button>
                <button className="ins-pbt" style={{ background:c.pagerBg, borderColor:c.pagerB, color:c.text }} onClick={()=>goPage(totalPages)} disabled={currentPage===totalPages}>»</button>
                <span style={{ fontSize:12, fontWeight:500, color:c.textDim, marginLeft:'auto' }}>
                  Page {currentPage}/{totalPages} · {meta.total} inscription{meta.total>1?'s':''}
                </span>
              </div>
            )}
          </div>
        </div>
      </main>

      {/*  MODAL  */}
      {showModal && (
        <div className="ins-mbg" onClick={closeModal}>
          <div className="ins-mbox" style={{ background:c.modalBg }} onClick={e=>e.stopPropagation()}>

            <div className="ins-mhead" style={{ background:c.modalHead, borderBottomColor:c.cardB }}>
              <div className="ins-mtabs">
                {[{id:'details',icon:<FaEye />,label:'Détails'},{id:'workflow',icon:<FaEdit />,label:'Traitement'},{id:'facture',icon:<FaFilePdf />,label:'Facture'}].map(t=>(
                  <button key={t.id} className="ins-mtab"
                    style={modalTab===t.id?{background:c.tabOn,color:isDark?c.title:'#fff',borderColor:c.tabOn}:{background:c.tabBg,color:c.textDim,borderColor:c.cardB}}
                    onClick={()=>setModalTab(t.id)}>{t.icon} {t.label}</button>
                ))}
              </div>
              <button className="ins-mcx" style={{ background:c.tabBg, borderColor:c.cardB, color:c.textDim }} onClick={closeModal}><FaTimes /></button>
            </div>

            <div className="ins-mscroll">
              {/* Loading modal */}
              {modalLoading ? (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'80px 20px', gap:12, color:c.textDim }}>
                  <FaSpinner style={{ animation:'spin 1s linear infinite', fontSize:24 }} />
                  <span style={{ fontSize:15 }}>Chargement…</span>
                </div>
              ) : selectedIns && (
                <>
                  {/*  ONGLET DÉTAILS  */}
                  {modalTab==='details' && (
                    <div className="ins-mpad">
                      <div className="ins-dhero" style={{ background:c.blockBg, borderColor:c.cardB }}>
                        <div className="ins-dhav" style={{ background:avColor(selectedIns.status) }}>{initials(selectedIns)}</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:18, fontWeight:700, color:c.title, marginBottom:3 }}>{selectedIns.civilite} {selectedIns.prenom} {selectedIns.nom}</div>
                          <div style={{ fontSize:12.5, color:c.textDim, marginBottom:10 }}>{selectedIns.poste} · {selectedIns.entreprise}</div>
                          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                            <span className="ins-bpill" style={{ color:STATUS_CFG[selectedIns.status]?.color, background:STATUS_CFG[selectedIns.status]?.bg, borderColor:STATUS_CFG[selectedIns.status]?.border }}>{STATUS_CFG[selectedIns.status]?.label}</span>
                            <span className="ins-bdot" style={{ color:PAY_CFG[selectedIns.paymentStatus]?.color, background:PAY_CFG[selectedIns.paymentStatus]?.bg }}>
                              <i style={{ background:PAY_CFG[selectedIns.paymentStatus]?.color }}/>{PAY_CFG[selectedIns.paymentStatus]?.label}
                            </span>
                            <span style={{ fontSize:11, fontWeight:600, color:c.textDim, background:c.surface, padding:'3px 9px', borderRadius:7, border:`1px solid ${c.cardB}` }}>{selectedIns.id}</span>
                          </div>
                        </div>
                        <div className="ins-dhamount">{fmtPrice(selectedIns.totalPrice)}</div>
                      </div>

                      <div className="ins-dg2">
                        <div className="ins-dbl" style={{ background:c.blockBg, borderColor:c.cardB }}>
                          <div className="ins-dbl-t" style={{ borderLeftColor:C.green, background:c.blockHd, borderBottomColor:c.cardB, color:c.text }}><FaUser /> Informations personnelles</div>
                          <div className="ins-drows">
                            {[['Email',selectedIns.email],['Téléphone',selectedIns.telephone],['Secteur',selectedIns.secteur],['Ville / Pays',`${selectedIns.ville}, ${selectedIns.pays}`]].map(([lbl,val])=>(
                              <div key={lbl} className="ins-drow"><label style={{ color:c.textDim }}>{lbl}</label><span style={{ color:c.title }}>{val}</span></div>
                            ))}
                          </div>
                        </div>
                        <div className="ins-dbl" style={{ background:c.blockBg, borderColor:c.cardB }}>
                          <div className="ins-dbl-t" style={{ borderLeftColor:C.orange, background:c.blockHd, borderBottomColor:c.cardB, color:c.text }}><FaGraduationCap /> Formation</div>
                          <div className="ins-drows">
                            <div className="ins-drow dfull"><label style={{ color:c.textDim }}>Intitulé</label><span style={{ color:c.title }}>{selectedIns.formation}</span></div>
                            <div className="ins-drow"><label style={{ color:c.textDim }}>Type</label>
                              <span className={`ins-tchip ins-tc-${selectedIns.typeFormation}`} style={{ width:'fit-content' }}>
                                {selectedIns.typeFormation==='presentiel'?<FaHome />:<FaDesktop />}{TYPE_LBL[selectedIns.typeFormation]||selectedIns.typeFormation}
                              </span>
                            </div>
                            <div className="ins-drow"><label style={{ color:c.textDim }}>Participants</label><span style={{ color:c.title }}>{selectedIns.nombrePersonnes} personne{selectedIns.nombrePersonnes>1?'s':''}</span></div>
                            <div className="ins-drow"><label style={{ color:c.textDim }}>Du → Au</label><span style={{ color:c.title }}>{fmtDate(selectedIns.startDate)} → {fmtDate(selectedIns.endDate)}</span></div>
                          </div>
                        </div>
                      </div>

                      {selectedIns.typeFormation==='presentiel' && (
                        <div className="ins-dbl" style={{ marginBottom:14, background:c.blockBg, borderColor:c.cardB }}>
                          <div className="ins-dbl-t" style={{ borderLeftColor:C.teal, background:c.blockHd, borderBottomColor:c.cardB, color:c.text }}><FaCheckCircle /> Services inclus</div>
                          <div className="ins-svcrow">
                            {[{k:'restauration',l:'Restauration'},{k:'hebergement',l:'Hébergement'},{k:'transport',l:'Transport'}].map(s=>(
                              <div key={s.k} className={`ins-svc ${selectedIns[s.k]?'ins-svc-y':''}`} style={!selectedIns[s.k]?{background:c.surface,color:c.textDim}:{}}>
                                {selectedIns[s.k]?<FaCheckCircle />:<FaTimesCircle />} {s.l}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="ins-dbl" style={{ marginBottom:14, background:c.blockBg, borderColor:c.cardB }}>
                        <div className="ins-dbl-t" style={{ borderLeftColor:C.red, background:c.blockHd, borderBottomColor:c.cardB, color:c.text }}><FaCreditCard /> Paiement</div>
                        <div className="ins-drows">
                          <div className="ins-drow"><label style={{ color:c.textDim }}>Mode</label><span style={{ color:c.title }}>{PAY_METHOD_LBL[selectedIns.modePaiement]||selectedIns.modePaiement}</span></div>
                          <div className="ins-drow"><label style={{ color:c.textDim }}>Total</label><span style={{ color:C.green, fontWeight:700, fontSize:17 }}>{fmtPrice(selectedIns.totalPrice)}</span></div>
                          <div className="ins-drow"><label style={{ color:c.textDim }}>Statut</label>
                            <span className="ins-bdot" style={{ color:PAY_CFG[selectedIns.paymentStatus]?.color, background:PAY_CFG[selectedIns.paymentStatus]?.bg }}>
                              <i style={{ background:PAY_CFG[selectedIns.paymentStatus]?.color }}/>{PAY_CFG[selectedIns.paymentStatus]?.label}
                            </span>
                          </div>
                          <div className="ins-drow"><label style={{ color:c.textDim }}>Certificat</label><span style={{ color:c.title }}>{selectedIns.certificat?'✓ Inclus':'✗ Non inclus'}</span></div>
                        </div>
                      </div>

                      <div className="ins-dbl" style={{ marginBottom:0, background:c.blockBg, borderColor:c.cardB }}>
                        <div className="ins-dbl-t" style={{ borderLeftColor:'#8fa3b5', background:c.blockHd, borderBottomColor:c.cardB, color:c.text }}><FaStickyNote /> Motivation déclarée</div>
                        <blockquote className="ins-motiv" style={{ color:c.text }}>« {selectedIns.motivation} »</blockquote>
                      </div>
                    </div>
                  )}

                  {/*  ONGLET WORKFLOW  */}
                  {modalTab==='workflow' && (
                    <div className="ins-mpad">
                      <div className="ins-wf-recap">
                        <div className="ins-wf-recap-left">
                          <FaBell className="ins-wf-bell"/>
                          <div>
                            <h3 style={{ fontSize:15, fontWeight:700, color:c.title, marginBottom:3 }}>
                              Traitement <span className="ins-wf-ref">{selectedIns.id}</span>
                            </h3>
                            <p style={{ fontSize:12.5, color:c.textDim }}>
                              Reçue le {fmtDate(selectedIns.inscriptionDate)} à {selectedIns.inscriptionTime} — {selectedIns.civilite} {selectedIns.prenom} {selectedIns.nom} ({selectedIns.entreprise})
                            </p>
                          </div>
                        </div>
                        <span className="ins-bpill" style={{ color:STATUS_CFG[selectedIns.status]?.color, background:STATUS_CFG[selectedIns.status]?.bg, borderColor:STATUS_CFG[selectedIns.status]?.border }}>
                          {STATUS_CFG[selectedIns.status]?.label}
                        </span>
                      </div>

                      {/* Checklist */}
                      <div className="ins-wf-sec" style={{ background:c.blockBg, borderColor:c.cardB }}>
                        <h4 className="ins-wf-sec-t" style={{ color:c.text }}><FaCheckDouble /> Checklist de traitement</h4>
                        <div className="ins-wf-checklist">
                          {[
                            { done:true,                                      label:'Inscription reçue dans le système'         },
                            { done:selectedIns.paymentStatus==='paid',        label:'Paiement reçu et confirmé'                 },
                            { done:selectedIns.status==='confirmed',          label:'Inscription validée et confirmée'           },
                            { done:!!selectedIns.convocationSent,             label:'Convocation envoyée au participant'         },
                            { done:selectedIns.certificat,                    label:'Certificat prévu pour cette inscription'    },
                          ].map((it,i)=>(
                            <div key={i} className={`ins-wfcli ${it.done?'ins-wfcli-done':'ins-wfcli-pending'}`}>
                              <div className="ins-wfcli-dot">{it.done?<FaCheck />:<FaClock />}</div>
                              <span>{it.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Statut inscription */}
                      <div className="ins-wf-sec" style={{ background:c.blockBg, borderColor:c.cardB }}>
                        <h4 className="ins-wf-sec-t" style={{ color:c.text }}><FaUserGraduate /> Statut de l'inscription</h4>
                        <div className="ins-wf-brow">
                          {[{s:'confirmed',cls:'ins-wfb-green',icon:<FaCheckCircle />,lbl:'Confirmer'},
                            {s:'pending',  cls:'ins-wfb-orange',icon:<FaClock />,      lbl:'En attente'},
                            {s:'cancelled',cls:'ins-wfb-red',  icon:<FaTimesCircle />, lbl:'Annuler'}].map(btn=>(
                            <button key={btn.s} className={`ins-wfb ${btn.cls} ${selectedIns.status===btn.s?'wfb-on':''}`}
                              disabled={isLoading(`${selectedIns.db_id}_status`)}
                              onClick={()=>changeStatus(selectedIns.db_id, btn.s)}>
                              {isLoading(`${selectedIns.db_id}_status`) ? <FaSpinner style={{ animation:'spin 1s linear infinite' }}/> : btn.icon}
                              {btn.lbl} {selectedIns.status===btn.s&&<span className="ins-wfb-cur">✓ Actuel</span>}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Statut paiement */}
                      <div className="ins-wf-sec" style={{ background:c.blockBg, borderColor:c.cardB }}>
                        <h4 className="ins-wf-sec-t" style={{ color:c.text }}><FaMoneyBillWave /> Statut du paiement</h4>
                        <div className="ins-wf-brow">
                          {[{s:'paid',   cls:'ins-wfb-green', icon:<FaMoneyBillWave />, lbl:'Marquer payé'},
                            {s:'pending',cls:'ins-wfb-orange',icon:<FaClock />,          lbl:'En attente'},
                            {s:'failed', cls:'ins-wfb-red',   icon:<FaTimesCircle />,    lbl:'Annuler'}].map(btn=>(
                            <button key={btn.s} className={`ins-wfb ${btn.cls} ${selectedIns.paymentStatus===btn.s?'wfb-on':''}`}
                              disabled={isLoading(`${selectedIns.db_id}_payment`)}
                              onClick={()=>changePayment(selectedIns.db_id, btn.s)}>
                              {isLoading(`${selectedIns.db_id}_payment`) ? <FaSpinner style={{ animation:'spin 1s linear infinite' }}/> : btn.icon}
                              {btn.lbl} {selectedIns.paymentStatus===btn.s&&<span className="ins-wfb-cur">✓ Actuel</span>}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Actions rapides */}
                      <div className="ins-wf-sec" style={{ background:c.blockBg, borderColor:c.cardB }}>
                        <h4 className="ins-wf-sec-t" style={{ color:c.text }}><FaPaperPlane /> Actions rapides</h4>
                        <div className="ins-wf-brow">
                          <button className={`ins-wfb ins-wfb-teal ${selectedIns.convocationSent?'wfb-on':''}`}
                            disabled={isLoading(`${selectedIns.db_id}_conv`)}
                            onClick={()=>sendConvocation(selectedIns.db_id)}>
                            {isLoading(`${selectedIns.db_id}_conv`) ? <FaSpinner style={{ animation:'spin 1s linear infinite' }}/> : <FaEnvelope />}
                            {selectedIns.convocationSent ? 'Renvoyer la convocation' : 'Envoyer la convocation'}
                          </button>
                          <button className="ins-wfb ins-wfb-dark" onClick={()=>setModalTab('facture')}><FaFilePdf /> Voir la facture</button>
                          <button className="ins-wfb ins-wfb-dark" onClick={()=>openInvoice(selectedIns)}><FaDownload /> Télécharger PDF</button>
                        </div>
                      </div>

                      {/* Notes */}
                      <div className="ins-wf-sec" style={{ background:c.blockBg, borderColor:c.cardB }}>
                        <h4 className="ins-wf-sec-t" style={{ color:c.text }}><FaStickyNote /> Notes internes</h4>
                        {(!selectedIns.notes||selectedIns.notes.length===0) && <p style={{ fontSize:13, color:c.textDim, marginBottom:12 }}>Aucune note pour l'instant.</p>}
                        {(selectedIns.notes||[]).map((n,i)=>(
                          <div key={i} className="ins-note" style={{ background:c.surface, borderColor:c.cardB }}>
                            <div className="ins-note-h">
                              <span className="ins-note-auth">{n.auteur}</span>
                              <span style={{ fontSize:11, color:c.textDim }}>{n.date}</span>
                            </div>
                            <p style={{ fontSize:13, color:c.text, lineHeight:1.6 }}>{n.texte}</p>
                          </div>
                        ))}
                        <textarea
                          style={{ width:'100%', padding:'11px 14px', border:`1.5px solid ${c.inputB}`, borderRadius:10, fontSize:13.5, color:c.title, resize:'vertical', outline:'none', background:c.noteArea, marginBottom:9, display:'block', fontFamily:"'Plus Jakarta Sans',sans-serif" }}
                          rows={3} value={noteText} onChange={e=>setNoteText(e.target.value)}
                          onFocus={e=>e.target.style.borderColor='#198754'} onBlur={e=>e.target.style.borderColor=c.inputB}
                          placeholder="Ajouter une note interne (visible uniquement par les admins)…"
                        />
                        <button className="ins-wfb ins-wfb-dark"
                          disabled={!noteText.trim()||isLoading(`${selectedIns.db_id}_note`)}
                          onClick={()=>addNote(selectedIns.db_id)}>
                          {isLoading(`${selectedIns.db_id}_note`) ? <FaSpinner style={{ animation:'spin 1s linear infinite' }}/> : <FaPaperPlane />}
                          Ajouter la note
                        </button>
                      </div>

                      {/* Historique */}
                      <div className="ins-wf-sec" style={{ background:c.blockBg, borderColor:c.cardB }}>
                        <h4 className="ins-wf-sec-t" style={{ color:c.text }}><FaHistory /> Historique des actions</h4>
                        <div className="ins-timeline">
                          {(selectedIns.historique||[]).map((h,i)=>(
                            <div key={i} className="ins-tli">
                              <div className="ins-tlidot" style={{ color:HISTO_CFG[h.icone]?.color||'#8fa3b5', borderColor:(HISTO_CFG[h.icone]?.color||'#8fa3b5')+'40', background:c.modalBg }}>●</div>
                              {i<(selectedIns.historique||[]).length-1&&<div className="ins-tliline" style={{ background:c.cardB }}/>}
                              <div className="ins-tlic">
                                <span style={{ fontSize:13.5, fontWeight:600, color:c.title }}>{h.action}</span>
                                <span style={{ fontSize:11.5, color:c.textDim }}>{h.date}{h.auteur&&h.auteur!=='Système'?` · ${h.auteur}`:''}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Zone dangereuse */}
                      <div className="ins-wf-sec ins-wf-danger" style={{ background:isDark?'rgba(220,53,69,.04)':'rgba(220,53,69,.02)', borderColor:'rgba(220,53,69,.15)' }}>
                        <h4 className="ins-wf-sec-t" style={{ color:C.red }}><FaExclamationTriangle /> Zone dangereuse</h4>
                        <p style={{ fontSize:13, color:c.textDim, marginBottom:12 }}>Cette action est irréversible. L'inscription sera définitivement supprimée.</p>
                        <button className="ins-wfb ins-wfb-out-red" onClick={()=>deleteIns(selectedIns.db_id)}><FaTrash /> Supprimer définitivement cette inscription</button>
                      </div>
                    </div>
                  )}

                  {/*  ONGLET FACTURE  */}
                  {modalTab==='facture' && (
                    <div className="ins-mpad">
                      <div className="ins-inv-actbar">
                        <button className="ins-wfb ins-wfb-green" onClick={()=>openInvoice(selectedIns)}><FaFilePdf /> Ouvrir et imprimer / enregistrer en PDF</button>
                        <button className="ins-wfb ins-wfb-dark"  onClick={()=>setModalTab('workflow')}><FaEdit /> Traiter l'inscription</button>
                      </div>
                      <p className="ins-inv-hint" style={{ color:c.text }}>
                        <FaExclamationTriangle style={{ color:C.orange }}/>
                        La facture s'ouvre dans un nouvel onglet. Utilisez <strong>Fichier → Imprimer → Enregistrer en PDF</strong>.
                      </p>
                      <div className="ins-inv-preview" style={{ background:c.card, borderColor:c.cardB }}>
                        <div className="ins-inv-head">
                          <div>
                            <img src={LOGO_SRC} alt="CINEF" className="ins-inv-logo" onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='block';}}/>
                            <div className="ins-inv-logo-fb">CINEF</div>
                            <p style={{ fontSize:11.5, color:c.text, marginBottom:1 }}>Centre International de Formation et d'Expertise</p>
                            <p style={{ fontSize:11, color:c.textDim }}>info@cinef-bj.com · +229 63 18 60 60</p>
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <div className="ins-inv-badge" style={{ color:c.title, borderColor:c.title }}>FACTURE</div>
                            <table className="ins-inv-meta-t">
                              <tbody>
                                {[['Numéro',selectedIns.id],['Émission',fmtDate(selectedIns.inscriptionDate)],['Formation',fmtDate(selectedIns.startDate)],['Règlement',PAY_METHOD_LBL[selectedIns.modePaiement]||selectedIns.modePaiement]].map(([l,v])=>(
                                  <tr key={l}><td style={{ color:c.text, textAlign:'left', fontWeight:600 }}>{l}</td><td style={{ color:c.textDim }}>{v}</td></tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <hr className="ins-inv-rule" style={{ borderTopColor:c.cardB }}/>
                        <div className="ins-inv-parties">
                          <div>
                            <p className="ins-inv-pe" style={{ color:c.textDim }}>Émetteur</p>
                            <p className="ins-inv-pn" style={{ color:c.title }}>CINEF Bénin</p>
                            <p style={{ fontSize:12, color:c.text, marginBottom:2 }}>Cotonou, Bénin</p>
                            <p style={{ fontSize:12, color:c.text }}>info@cinef-bj.com</p>
                          </div>
                          <div>
                            <p className="ins-inv-pe" style={{ color:c.textDim }}>Destinataire</p>
                            <p className="ins-inv-pn" style={{ color:c.title }}>{selectedIns.civilite} {selectedIns.prenom} {selectedIns.nom}</p>
                            <p style={{ fontSize:12, color:c.text, marginBottom:2 }}>{selectedIns.poste} — {selectedIns.entreprise}</p>
                            <p style={{ fontSize:12, color:c.text, marginBottom:2 }}>{selectedIns.ville}, {selectedIns.pays}</p>
                            <p style={{ fontSize:12, color:c.text }}>{selectedIns.email}</p>
                          </div>
                        </div>
                        <hr className="ins-inv-rule" style={{ borderTopColor:c.cardB }}/>
                        <table className="ins-inv-ltbl">
                          <thead style={{ background:c.blockBg }}>
                            <tr>
                              <th style={{ width:'46%', color:c.textDim }}>Description</th>
                              <th style={{ textAlign:'center', color:c.textDim }}>Qté</th>
                              <th style={{ textAlign:'right', color:c.textDim }}>Prix unit.</th>
                              <th style={{ textAlign:'right', color:c.textDim }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getInvoiceLines(selectedIns).map((l,i)=>(
                              <tr key={i} style={{ background:i%2===0?c.blockBg:c.card }}>
                                <td style={{ borderBottomColor:c.cardB }}><strong className="ins-inv-ln" style={{ color:c.title }}>{l.desc}</strong><span className="ins-inv-ls" style={{ color:c.textDim }}>{l.sub}</span></td>
                                <td style={{ textAlign:'center', fontWeight:700, color:c.title, borderBottomColor:c.cardB }}>{l.qty}</td>
                                <td style={{ textAlign:'right', color:c.text, borderBottomColor:c.cardB }}>{fmtPrice(l.pu)}</td>
                                <td style={{ textAlign:'right', fontWeight:700, color:c.title, borderBottomColor:c.cardB }}>{fmtPrice(l.qty*l.pu)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <hr className="ins-inv-rule" style={{ borderTopColor:c.cardB }}/>
                        <div className="ins-inv-tots" style={{ borderColor:c.cardB }}>
                          <div className="ins-inv-tr" style={{ borderBottom:`1px solid ${c.cardB}` }}><span style={{ color:c.textDim }}>Sous-total HT</span><span style={{ color:c.title }}>{fmtPrice(selectedIns.totalPrice)}</span></div>
                          <div className="ins-inv-tr" style={{ borderBottom:`1px solid ${c.cardB}` }}><span style={{ color:c.textDim }}>TVA (18 %)</span><span style={{ color:c.title }}>{fmtPrice((selectedIns.totalPrice || 0) * 0.18)}</span></div>
                          <div className="ins-inv-tgrand"><span>TOTAL TTC</span><span style={{ color:C.teal }}>{fmtPrice((selectedIns.totalPrice || 0) * 1.18)}</span></div>
                        </div>
                        <div style={{ fontSize:12, color:C.green, padding:'11px 14px', background:'rgba(34,197,94,0.1)', borderLeft:`3px solid ${C.green}`, borderRadius:8, marginBottom:10, lineHeight:1.7 }}>
                          <strong>✓ Services inclus :</strong> Pauses-café et Déjeuner offerts • Support de formation
                        </div>
                        <div style={{ fontSize:12, color:c.text, padding:'11px 14px', background:c.blockBg, borderRadius:8, marginBottom:5, lineHeight:1.8 }}>
                          <strong>Mode de règlement :</strong> {PAY_METHOD_LBL[selectedIns.modePaiement]||selectedIns.modePaiement}<br/>
                          <strong>Conditions :</strong> Paiement intégral avant le début de la formation ({fmtDate(selectedIns.startDate)}).
                        </div>
                        <hr className="ins-inv-rule" style={{ borderTopColor:c.cardB }}/>
                        <p style={{ fontSize:11, color:c.textDim, textAlign:'center', paddingTop:4 }}>Merci pour votre confiance. CINEF — Centre International de Formation et d'Expertise</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inscriptions;